/**
 * Paragraph-based in-article ad placement, generalized from the legacy
 * ArticleListInfinite.tsx logic (computeParagraphMap / shouldShowAdAfterParagraph /
 * getAdPositionForParagraph) to cycle through a template's configured
 * InArticleAdSlot[] (raw desktop/mobile ad codes) instead of the AdSlotConfig
 * ros-inread/multiple-300x250 store entities.
 */

import { InArticleAdSlot } from './types/articleTemplate';

export interface ParagraphInfo {
  startIndex: number;
  paragraphs: string[];
}

/**
 * Split HTML text into top-level block elements (<p>, <ul>, <ol>, <div>,
 * <blockquote>, <h1>-<h6>, <table>). Preserves <ul>/<ol> as single blocks so
 * list structure isn't broken.
 */
export function splitIntoParagraphs(html: string): string[] {
  if (!html) return [];
  const matches = html.match(/<(p|ul|ol|div|blockquote|h[1-6]|table)[\s\S]*?<\/\1>/gi);
  return matches || [];
}

/**
 * Build a "cardIdx_elementIdx" -> 0-indexed image position map for a story,
 * counting only standalone `type: 'image'` story-elements (the single full-
 * width image + caption block every Article*Body renders inline — NOT the
 * grouped `composite/image-gallery` element, which is its own carousel/grid
 * unit and isn't a body-flow anchor point).
 *
 * This is a SEPARATE counter from computeParagraphMap's text-paragraph
 * index — the two must never share index space. A photo-gallery article
 * (all images, zero text paragraphs) has nothing for the Builder-configured
 * "after paragraph N" ad slots to anchor to, so buildInlineAdRenderer falls
 * back to this map and treats N as "after the Nth image" instead. Mixing
 * this into the same counter as computeParagraphMap would renumber every
 * existing mixed text+image article's ad positions (an image between
 * paragraph 1 and 2 would shift "after paragraph 2" to land on the image).
 */
export function computeImageIndexMap(story: any): Record<string, number> {
  const map: Record<string, number> = {};
  let globalImageIdx = 0;
  for (let cardIdx = 0; cardIdx < (story?.cards?.length || 0); cardIdx++) {
    const card = story.cards[cardIdx];
    for (let elemIdx = 0; elemIdx < (card['story-elements']?.length || 0); elemIdx++) {
      const element = card['story-elements'][elemIdx];
      if (element.type === 'image' && element['image-s3-key']) {
        map[`${cardIdx}_${elemIdx}`] = globalImageIdx;
        globalImageIdx += 1;
      }
    }
  }
  return map;
}

/**
 * Build a "cardIdx_elementIdx" -> { startIndex, paragraphs } map for a story,
 * counting only regular body-text elements (excludes summary/also-read/
 * promotional/question/quote/blockquote/cta/q-and-a/bigfact/blurb). These
 * subtypes render as their own highlighted card via a separate code path
 * that never consults this map or calls renderInlineAd — counting them here
 * would reserve global paragraph indices that can never fire an ad, silently
 * swallowing whichever configured `afterParagraph` positions happened to
 * land on one of those instead of a regular paragraph. `answer` is
 * deliberately NOT excluded — in the Q&A template it IS the regular flowing
 * body text and must keep its normal ad-eligible paragraph indices.
 */
export function computeParagraphMap(story: any): Record<string, ParagraphInfo> {
  const map: Record<string, ParagraphInfo> = {};
  let globalParaIdx = 0;
  for (let cardIdx = 0; cardIdx < (story?.cards?.length || 0); cardIdx++) {
    const card = story.cards[cardIdx];
    for (let elemIdx = 0; elemIdx < (card['story-elements']?.length || 0); elemIdx++) {
      const element = card['story-elements'][elemIdx];
      if (
        element.type === 'text' &&
        element.text &&
        element.subtype !== 'summary' &&
        element.subtype !== 'also-read' &&
        element.subtype !== 'question' &&
        element.subtype !== 'quote' &&
        element.subtype !== 'blockquote' &&
        element.subtype !== 'cta' &&
        element.subtype !== 'q-and-a' &&
        element.subtype !== 'bigfact' &&
        element.subtype !== 'blurb' &&
        !element.metadata?.['promotional-message']
      ) {
        const paragraphs = splitIntoParagraphs(element.text);
        if (paragraphs.length > 0) {
          map[`${cardIdx}_${elemIdx}`] = { startIndex: globalParaIdx, paragraphs };
          globalParaIdx += paragraphs.length;
        }
      }
    }
  }
  return map;
}

/**
 * Given the template's configured inArticleAds, build a lookup from global
 * paragraph index -> the ad slot that should render after that paragraph.
 *
 * Each configured slot is a ONE-TIME placement, not a repeating pattern —
 * `afterParagraph` is the single, exact paragraph it appears after, and
 * that's the only time it shows in the article. Six configured slots means
 * exactly six possible ad placements total, at six specific paragraphs
 * (e.g. afterParagraph 2, 6, 9, 12, 15, 18) — never looping/repeating past
 * that single position, and simply not appearing at all if the article has
 * fewer paragraphs than a slot's configured position.
 *
 * `afterParagraph` is human-facing and 1-indexed ("3" means after the 3rd
 * paragraph). The internal paragraph index (`globalParaIdx`, from
 * computeParagraphMap) is 0-indexed — paragraph 1 is index 0 — so it's
 * converted with `- 1`. `afterParagraph: 0` is a special case meaning
 * "before the first paragraph" (top of body, above all text) — it converts
 * to position `-1`, a lookup key callers check explicitly before their
 * paragraph loop starts (see renderInlineAd?.(-1) in each Article*Body).
 * Previously `0` was falsy and silently filtered out entirely, dropping the
 * configured ad instead of placing it at the top.
 *
 * Ties (two slots configured for the same paragraph) resolve in configured
 * array order — the first slot wins that position.
 *
 * `hasParagraphs` covers templates like video/gumlet-video, whose articles
 * are frequently just a player with no body text at all — every configured
 * slot's `afterParagraph` position (1, 2, ...) targets a paragraph that will
 * never exist, so it would otherwise be silently dropped even though every
 * Article*Body still unconditionally calls `renderInlineAd?.(-1)` at the top
 * of its body before the (empty) paragraph loop. When there are no
 * paragraphs to anchor to, place the lowest-configured slot at that top-of-
 * body position instead, so the article isn't left with zero ads just
 * because it has no text.
 */
/** Whether a slot has actual content to show, checking the field pair that
 *  matches its contentType (default 'ad') — desktopCode/mobileCode for 'ad',
 *  desktopImage/mobileImage for 'image', desktopHtml/mobileHtml for 'html'. */
function hasSlotContent(slot: InArticleAdSlot): boolean {
  switch (slot.contentType) {
    case 'image': return !!(slot.desktopImage || slot.mobileImage);
    case 'html': return !!(slot.desktopHtml || slot.mobileHtml);
    default: return !!(slot.desktopCode || slot.mobileCode);
  }
}

export function buildParagraphAdPlan(adSlots: InArticleAdSlot[], hasParagraphs: boolean = true): Map<number, InArticleAdSlot> {
  const plan = new Map<number, InArticleAdSlot>();
  const validSlots = (adSlots || []).filter(s => hasSlotContent(s) && s.afterParagraph !== undefined && s.afterParagraph !== null && s.afterParagraph >= 0);

  if (!hasParagraphs) {
    if (validSlots.length > 0) {
      const firstSlot = validSlots.reduce((lowest, s) => (s.afterParagraph! < lowest.afterParagraph! ? s : lowest));
      plan.set(-1, firstSlot);
    }
    return plan;
  }

  for (const slot of validSlots) {
    const position = slot.afterParagraph! - 1;
    if (!plan.has(position)) {
      plan.set(position, slot);
    }
  }
  return plan;
}

export function shouldShowAdAfterParagraph(plan: Map<number, InArticleAdSlot>, globalParaIdx: number): boolean {
  return plan.has(globalParaIdx);
}

export function getAdSlotForParagraph(plan: Map<number, InArticleAdSlot>, globalParaIdx: number): InArticleAdSlot | null {
  return plan.get(globalParaIdx) || null;
}
